import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const api = axios.create({
  baseURL: "https://modularobjects.com/checkout/api/project-delivery",
});

function waitForAuthUser() {
  const auth = getAuth();

  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user || null);
    });
  });
}

api.interceptors.request.use(async (config) => {
  const user = await waitForAuthUser();

  console.log("API request:", {
    baseURL: config.baseURL,
    url: config.url,
    method: config.method,
    params: config.params,
    hasUser: !!user,
  });

  if (user) {
    const token = await user.getIdToken();
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn("No Firebase user when calling API:", config.url);
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    console.log("API response:", {
      url: response.config.url,
      status: response.status,
      data: response.data,
    });

    return response;
  },
  (error) => {
    console.error("API error:", {
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });

    return Promise.reject(error);
  }
);

export async function fetchDeliveryProjects() {
  const res = await api.get("/projects");
  return res.data?.items || [];
}

export async function fetchProjectScans(projectKey) {
  const res = await api.get(`/projects/${encodeURIComponent(projectKey)}/scans`);
  return {
    session: res.data?.session || null,
    items: res.data?.items || [],
  };
}

export async function saveProjectScan(projectKey, payload) {
  const res = await api.post(
    `/projects/${encodeURIComponent(projectKey)}/scans`,
    payload
  );
  return res.data?.item;
}

export async function closeProjectSession(projectKey) {
  const res = await api.post(
    `/projects/${encodeURIComponent(projectKey)}/close-session`
  );
  return res.data?.session || null;
}

export async function fetchProjectDocumentFolders(projectKey) {
  const res = await api.get(
    `/projects/${encodeURIComponent(projectKey)}/document-folders`
  );
  return res.data?.items || [];
}

export async function fetchProjectBoards(projectKey, documentFolderKey = null) {
  const res = await api.get(
    `/projects/${encodeURIComponent(projectKey)}/boards`,
    {
      params: documentFolderKey ? { documentFolderKey } : {},
    }
  );
  return res.data?.items || [];
}

export async function fetchProjectOverrides(projectKey) {
  const res = await api.get(
    `/projects/${encodeURIComponent(projectKey)}/overrides`
  );
  return res.data?.items || [];
}

export async function saveProjectOverride(projectKey, payload) {
  const res = await api.post(
    `/projects/${encodeURIComponent(projectKey)}/overrides`,
    payload
  );
  return res.data?.item || null;
}

export async function removeProjectOverride(projectKey, boardCode) {
  await api.delete(
    `/projects/${encodeURIComponent(projectKey)}/overrides/${encodeURIComponent(boardCode)}`
  );
}

export async function fetchProjectAuditLog(projectKey, limit = 200) {
  const res = await api.get(
    `/projects/${encodeURIComponent(projectKey)}/audit-log`,
    { params: { limit } }
  );
  return res.data?.items || [];
}

export async function fetchProjectSummary(projectKey, documentFolderKey = null) {
  const res = await api.get(
    `/projects/${encodeURIComponent(projectKey)}/summary`,
    {
      params: documentFolderKey ? { documentFolderKey } : {},
    }
  );

  return {
    summary: res.data?.summary || null,
    rows: res.data?.rows || [],
    unknownScans: res.data?.unknownScans || [],
  };
}

export async function fetchProjectClaims(projectKey) {
  const res = await api.get(
    `/projects/${encodeURIComponent(projectKey)}/claims`
  );
  return res.data?.items || [];
}

export async function createProjectClaim(projectKey, payload) {
  const res = await api.post(
    `/projects/${encodeURIComponent(projectKey)}/claims`,
    payload
  );
  return res.data?.item || null;
}